import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Calculator, FileText, DollarSign, TrendingUp, Building, BookOpen, Star, ArrowRight, Award } from 'lucide-react';
import { ENHANCED_PROJECT_TYPES } from '../utils/advancedSEO';
import { getAllBlogPosts } from '../data/blogData';

const HomePage = () => {
  const features = [
    {
      title: 'Conceptual Cost Estimate',
      description: 'Get quick cost estimates based on your project specifications through our guided questionnaire.',
      icon: Calculator,
      link: '/conceptual-estimate',
      color: 'bg-blue-500'
    },
    {
      title: 'Drawing-based Estimate',
      description: 'Upload your construction drawings for detailed cost analysis and estimation.',
      icon: FileText,
      link: '/drawing-estimate',
      color: 'bg-green-500'
    },
    {
      title: 'Quote Checker',
      description: 'Compare and validate contractor quotes against market standards.',
      icon: DollarSign,
      link: '/quote-checker',
      color: 'bg-purple-500'
    },
    {
      title: 'Quote Changes',
      description: 'Track and manage changes to your construction quotes over time.',
      icon: TrendingUp,
      link: '/quote-changes',
      color: 'bg-orange-500'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Construction Cost Calculator | Free AI-Powered Estimates 2024</title>
        <meta name="description" content="Get accurate construction cost estimates with our AI-powered calculator. Free instant quotes for homes, buildings, and renovations across all 50 states." />
        <meta name="keywords" content="construction cost calculator, building cost estimator, home construction cost, construction estimate, building calculator, cost per square foot, construction pricing" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "Construction Cost Calculator",
            "description": "AI-powered construction cost estimation tool",
            "url": "https://costtoconstruct.com",
            "applicationCategory": "FinanceApplication",
            "operatingSystem": "Web",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            }
          })}
        </script>
      </Helmet>
      
    <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <div className="relative bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-800 overflow-hidden">
          <div className="absolute inset-0 bg-black opacity-20"></div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
            <div className="text-center">
              <h1 className="text-4xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                Build Your Dream Project
                <span className="block text-blue-200">Know the Real Cost</span>
              </h1>
              <p className="text-xl lg:text-2xl text-blue-100 max-w-4xl mx-auto mb-8 leading-relaxed">
                Get instant, AI-powered construction cost estimates for any project. 
                From single-family homes to commercial buildings - accurate, fast, and free.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <Link
                  to="/conceptual-estimate"
                  className="bg-white text-blue-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-blue-50 transition-colors duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                >
                  <Calculator className="inline mr-2" size={20} />
                  Start Free Estimate
                </Link>
                <Link
                  to="/drawing-estimate"
                  className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white hover:text-blue-600 transition-colors duration-300"
                >
                  Upload Drawings
                </Link>
              </div>
            </div>
          </div>
          {/* Decorative elements */}
          <div className="absolute top-0 right-0 -mt-20 -mr-20 w-80 h-80 bg-blue-500 rounded-full opacity-10"></div>
          <div className="absolute bottom-0 left-0 -mb-20 -ml-20 w-60 h-60 bg-indigo-400 rounded-full opacity-10"></div>
        </div>

        {/* Features Section */}
        <div className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                Everything You Need for Construction Planning
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Professional-grade tools to estimate, plan, and manage your construction projects with confidence.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {features.map((feature, index) => (
                <div key={index} className="group relative bg-white rounded-xl p-8 shadow-sm hover:shadow-lg transition-all duration-300 border border-gray-100">
                  <div className={`inline-flex items-center justify-center w-12 h-12 rounded-lg ${feature.color} text-white mb-6`}>
                    <feature.icon size={24} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">
                    {feature.description}
                  </p>
                  <Link
                    to={feature.link}
                    className="inline-flex items-center text-blue-600 font-semibold hover:text-blue-700 transition-colors duration-300"
                  >
                    Get Started
                    <svg className="ml-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Project Types Section */}
        <div className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                What Are You Building?
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Choose your project type to get started with accurate cost estimates tailored to your specific needs.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {Object.entries(ENHANCED_PROJECT_TYPES).map(([typeId, typeData]) => (
                <Link
                  key={typeId}
                  to={`/estimate/${typeId}/california`}
                  className="group relative bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-100 overflow-hidden"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-indigo-50 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="relative p-6">
                    <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl mb-4 group-hover:scale-110 transition-transform duration-300">
                      <Building className="text-white" size={24} />
                    </div>
                    <h4 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors duration-300">
                      {typeData.label}
                    </h4>
                    <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                      Professional cost estimation with detailed breakdowns and accurate pricing.
                    </p>
                    <div className="flex items-center text-blue-600 font-semibold group-hover:text-blue-700 transition-colors duration-300">
                      <span className="text-sm">Get Estimate</span>
                      <svg className="ml-2 w-4 h-4 transform group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                      </svg>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* Construction Markets Section */}
        <div className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                Popular Construction Markets
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Explore construction costs in top markets across the United States with real market data.
              </p>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { state: 'California', note: 'High-cost market', premium: '+25%' },
                { state: 'Texas', note: 'Moderate costs', premium: '-5%' },
                { state: 'Florida', note: 'Growing market', premium: '+10%' },
                { state: 'New York', note: 'Premium market', premium: '+30%' },
                { state: 'Illinois', note: 'Stable costs', premium: '+5%' },
                { state: 'Pennsylvania', note: 'Moderate costs', premium: '+0%' },
                { state: 'Ohio', note: 'Lower costs', premium: '-10%' },
                { state: 'Georgia', note: 'Growing market', premium: '+5%' }
              ].map(stateInfo => (
                <Link
                  key={stateInfo.state}
                  to={`/estimate/single-family/${stateInfo.state.toLowerCase().replace(/\s+/g, '-')}`}
                  className="group bg-white p-3 rounded-lg shadow-sm hover:shadow-md border border-gray-100 hover:border-green-200 transition-all duration-300"
                >
                  <div className="text-center">
                    <h4 className="text-sm font-semibold text-gray-900 mb-1">{stateInfo.state}</h4>
                    <p className="text-xs text-gray-600 mb-1">{stateInfo.note}</p>
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium">
                      {stateInfo.premium}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* Benefits Section */}
        <div className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                Why Choose Our Cost Estimation Platform?
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Save time, reduce costs, and make informed decisions with our AI-powered construction cost estimation tools.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                  <TrendingUp className="text-green-600" size={32} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Accurate Estimates</h3>
                <p className="text-gray-600">Get precise cost estimates with up to 99% accuracy using real market data and AI analysis.</p>
              </div>
              <div className="text-center">
                <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Calculator className="text-blue-600" size={32} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Instant Results</h3>
                <p className="text-gray-600">Get detailed cost breakdowns in minutes, not days. Start planning your project immediately.</p>
              </div>
              <div className="text-center">
                <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                  <DollarSign className="text-purple-600" size={32} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Completely Free</h3>
                <p className="text-gray-600">No hidden fees, no subscriptions. Access professional-grade estimation tools at no cost.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Best Tools Blog Section */}
        <div className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <div className="inline-flex items-center bg-yellow-100 text-yellow-800 px-4 py-2 rounded-full mb-4">
                <Award className="mr-2" size={20} />
                <span className="font-semibold">Expert Reviews & Comparisons</span>
              </div>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                Best Construction Tools & Software {new Date().getFullYear()}
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Discover top-rated construction tools with our comprehensive reviews. 
                MeltPlan ranks #1 across multiple categories.
              </p>
            </div>

            {/* Featured Blog Posts */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
              {getAllBlogPosts().slice(0, 3).map((blog, index) => (
                <Link
                  key={blog.slug}
                  to={`/blog/${blog.slug}`}
                  className="group bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border-2 border-gray-100 hover:border-blue-300"
                >
                  {/* Header Badge */}
                  <div className={`px-6 py-4 ${index === 0 ? 'bg-gradient-to-r from-yellow-400 to-orange-400' : 'bg-gradient-to-r from-blue-500 to-indigo-600'}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-white font-bold text-sm">
                        {index === 0 ? '🏆 MOST POPULAR' : '⭐ TOP RATED'}
                      </span>
                      <div className="flex items-center text-white text-xs">
                        <Star className="fill-white" size={14} />
                        <span className="ml-1">4.9/5</span>
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors line-clamp-2 min-h-[3.5rem]">
                      {blog.title}
                    </h3>

                    <p className="text-sm text-gray-600 mb-4 line-clamp-3">
                      {blog.introduction.substring(0, 120)}...
                    </p>

                    {/* Key Highlight */}
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
                      <div className="flex items-start">
                        <Award className="text-green-600 mr-2 flex-shrink-0 mt-0.5" size={16} />
                        <div>
                          <p className="text-xs font-semibold text-green-900 mb-1">MeltPlan - #1 Ranked</p>
                          <p className="text-xs text-green-700">AI-powered, fastest, most accurate</p>
                        </div>
                      </div>
                    </div>

                    {/* CTA */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-blue-600 font-semibold group-hover:text-blue-700 transition-colors">
                        <span className="text-sm">Read Review</span>
                        <ArrowRight className="ml-2 transform group-hover:translate-x-1 transition-transform" size={16} />
                      </div>
                      <span className="text-xs text-gray-500">{blog.readTime}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {/* View All Blogs CTA */}
            <div className="text-center">
              <Link
                to="/blog"
                className="inline-flex items-center bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-8 py-4 rounded-xl font-bold hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
              >
                <BookOpen className="mr-2" size={20} />
                View All Tool Reviews
                <ArrowRight className="ml-2" size={20} />
              </Link>
            </div>

            {/* Quick Stats */}
            <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="text-center p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl">
                <div className="text-3xl font-bold text-blue-600 mb-2">10+</div>
                <p className="text-sm text-gray-600">Tools Reviewed</p>
              </div>
              <div className="text-center p-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl">
                <div className="text-3xl font-bold text-green-600 mb-2">#1</div>
                <p className="text-sm text-gray-600">MeltPlan Ranking</p>
              </div>
              <div className="text-center p-4 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl">
                <div className="text-3xl font-bold text-purple-600 mb-2">100%</div>
                <p className="text-sm text-gray-600">Unbiased Reviews</p>
              </div>
              <div className="text-center p-4 bg-gradient-to-br from-orange-50 to-red-50 rounded-xl">
                <div className="text-3xl font-bold text-orange-600 mb-2">{new Date().getFullYear()}</div>
                <p className="text-sm text-gray-600">Latest Updates</p>
              </div>
            </div>
          </div>
        </div>

        {/* Call to Action Section */}
        <div className="py-20 bg-gradient-to-r from-blue-600 to-indigo-700">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Ready to Build Your Dream Project?
            </h2>
            <p className="text-xl text-blue-100 mb-10 max-w-3xl mx-auto">
              Join thousands of builders, contractors, and homeowners who trust our AI-powered cost estimation platform.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
              <Link
                to="/conceptual-estimate"
                className="bg-white text-blue-600 px-10 py-4 rounded-xl text-lg font-semibold hover:bg-blue-50 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
              >
                <Calculator className="inline mr-2" size={20} />
                Start Free Estimate
              </Link>
              <Link
                to="/drawing-estimate"
                className="border-2 border-white text-white px-10 py-4 rounded-xl text-lg font-semibold hover:bg-white hover:text-blue-600 transition-all duration-300"
              >
                <FileText className="inline mr-2" size={20} />
                Upload Your Drawings
              </Link>
            </div>
          </div>
        </div>

        {/* Statistics Section */}
        <div className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h3 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-4">
                Trusted by Construction Professionals Nationwide
              </h3>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="text-3xl lg:text-4xl font-bold text-blue-600 mb-2">99%</div>
                <p className="text-gray-600">Accuracy Rate</p>
              </div>
              <div className="text-center">
                <div className="text-3xl lg:text-4xl font-bold text-green-600 mb-2">50+</div>
                <p className="text-gray-600">States Covered</p>
              </div>
              <div className="text-center">
                <div className="text-3xl lg:text-4xl font-bold text-purple-600 mb-2">10K+</div>
                <p className="text-gray-600">Projects Estimated</p>
              </div>
              <div className="text-center">
                <div className="text-3xl lg:text-4xl font-bold text-orange-600 mb-2">24/7</div>
                <p className="text-gray-600">Available</p>
              </div>
            </div>
          </div>
        </div>
    </div>
    </>
  );
};

export default HomePage;
